// contactFormUtils.js
import { sanitizeForm } from '../../../Utilities/DataCleanUp';

/**
 * Prepares form data for contact submission, handling both regular fields and file uploads
 * @param {Object} contact - Raw contact form data
 * @param {Object} currentUser - Current user information
 * @returns {FormData} Prepared FormData object ready for submission
 */
export const prepareContactFormData = (contact, currentUser) => {
    const formData = new FormData();

    // Create a sanitized copy of the contact data
    const sanitizedContact = sanitizeForm({
        ...contact,
        user_id: currentUser.id,
    });

    // Process all fields except proof_of_life
    const processedContact = processContactFields(sanitizedContact);

    // Handle file upload separately
    handleProofOfLife(processedContact, formData);

    // Add the processed contact data to the FormData object
    formData.append('contact', JSON.stringify(processedContact));

    return formData;
};

/**
 * Processes all contact fields, ensuring proper formatting and data types
 * @param {Object} contact - Sanitized contact data
 * @returns {Object} Processed contact data with standardized field values
 */
const processContactFields = (contact) => {
    const processed = { ...contact };

    // Convert undefined/null values to empty strings for all fields except proof_of_life
    Object.keys(processed).forEach(key => {
        if (key === 'proof_of_life') return;
        processed[key] = processed[key] ?? '';
    });

    return processed;
};

/**
 * Handles the proof of life file field, adding it to FormData if present
 * @param {Object} contact - Contact data object
 * @param {FormData} formData - FormData instance to append the file to
 */
const handleProofOfLife = (contact, formData) => {
    if (contact.proof_of_life instanceof File) {
        formData.append('file', contact.proof_of_life);
        contact.proof_of_life = ''; // Clear from JSON to avoid duplicate data
    } else {
        contact.proof_of_life = '';
    }
};

/**
 * Validates required contact fields
 * @param {Object} data - Contact form data to validate
 * @returns {Object} Object containing any validation errors
 */
export const validateContactForm = (data) => {
    const errors = {};

    // Required field validation
    const requiredFields = {
        primary_emergency_contact_name: 'Primary Emergency Contact Name',
        primary_emergency_contact_number: 'Primary Emergency Contact Number'
    };

    Object.entries(requiredFields).forEach(([field, label]) => {
        if (!data[field]?.toString().trim()) {
            errors[field] = `${label} is required`;
        }
    });

    return errors;
};

/**
 * Checks if the form has any required fields filled
 * @param {Object} data - Contact form data to check
 * @returns {boolean} True if form has required data, false if completely empty
 */
export const isFormEmpty = (data) => {
    const requiredFields = ['primary_emergency_contact_name', 'primary_emergency_contact_number'];

    return requiredFields.every(field => !data[field]?.toString().trim());
};

/**
 * Initializes empty contact form data with default values
 * @returns {Object} Initial contact form data structure
 */
export const initializeContactForm = () => ({
    call_sign: '',
    office_phone: '',
    dect_phone: '',
    mobile_phone: '',
    sat_number: '',
    primary_emergency_contact_name: '',
    primary_emergency_contact_number: '',
    secondary_emergency_contact_name: '',
    secondary_emergency_contact_number: '',
    proof_of_life: null,
});